import React from 'react'
import { Chip } from '@mui/material'
import CustomTooltip from '../CustomTooltip'

interface SandboxBadgeProps {
  /** The term's own sandbox flag. Absent or false renders nothing. */
  isSandbox?: boolean
  size?: 'small' | 'medium'
}

/**
 * Marks a Survey Project, or one of its surveys, as running in sandbox (CR-341).
 *
 * Call sites: the Instructor Dashboard card, the student survey card, the survey-taking page and
 * the Reports title row. The last three were dropped on 2026-09-18, when the backend froze a
 * sandboxed
 * term at Published — no distributions, so no student row to badge — and restored on 2026-09-21
 * when the full-cycle spec withdrew that freeze: a rehearsal now reaches the people rehearsing
 * it, and those surfaces can be showing one with nothing else to say so.
 *
 * Three places deliberately do not use it. The Survey Project page header dropped its badge on
 * 2026-09-20, because the page banner names the mode in a sentence a hand below and the chip was
 * the same thing said twice. The terms table uses a tooltipped flask in the Term cell instead,
 * since a second chip beside the row's status chip reads as a second status. And the Projects
 * sidebar carries no badge at all: sandbox is per term, a project is not sandboxed, and a row
 * badged for a project whose terms all happen to be rehearsals was tried on 2026-09-22 and
 * withdrawn the same day.
 *
 * Returns `null` when not sandboxed, so call sites render it unconditionally.
 *
 * Tooltipped, and deliberately **not** `pointerEvents: 'none'` — which is what it carried until
 * 2026-09-23 and what made it the one badge on these surfaces that did nothing under the cursor.
 * The terms table's flask has explained itself on hover since it was written; this is the same
 * marker and now says the same thing.
 */
export const SANDBOX_TOOLTIP =
  'Sandbox — for rehearsing a survey end to end before the real one. Only system-level users receive it, and its results stay out of reports.'

const SandboxBadge: React.FC<SandboxBadgeProps> = ({ isSandbox, size = 'small' }) => {
  if (isSandbox !== true) return null

  return (
    <CustomTooltip title={SANDBOX_TOOLTIP}>
      <Chip
        label="Sandbox"
        size={size}
        color="warning"
        variant="outlined"
        sx={{ fontWeight: 600 }}
      />
    </CustomTooltip>
  )
}

export default SandboxBadge
